import React from 'react';
import {trim} from 'lodash';
import {toStringId} from '@packages/systems/core/utils/HtmlUtils';
import {
  Expressions,
  withSystem,
  withMeta,
  Plugins,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  ContextualExpressions,
} from '@packages/utilities/contextual-expression';
import {
  ExpressionEditor,
  updateExpressionsAction,
  extendExpressionEditorActionAtPath,
} from '@packages/systems/ExpressionEditor-utils';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  TRecord,
  TText,
  TEnum,
  TBoolean,
  TTypeConstructor,
  EText,
} from '@packages/systems/wfdl-ast';
import {
  FORM_NAME_PATH,
  INPUT_TYPE_PATH,
  ATTR_REQUIRED_PATH,
  ATTR_ID_PATH,
  ATTR_CHECKED_PATH,
  ATTR_NAME_PATH,
  ATTR_DATA_NAME_PATH,
  CHECKBOX_DEFAULT,
  CHECKBOX_DEFAULT_CAP,
} from '../constants';
import {emotionStyled, tokens} from '@packages/utilities/styled';
import {createDevLinkRootAlias} from '@packages/utilities/integrated-types';

const {getIn} = ContextualExpressions;

const Grid = emotionStyled('div')({
  display: 'grid',
  gap: tokens.space[2],
  marginBottom: tokens.space[2],
});

const formFields = TRecord({
  type: withSystem({defaultValue: 'checkbox-input', private: true}, TText),
  name: withMeta(
    {placeholder: 'e.g. Checkbox'},
    withSystem(
      {label: 'Name', defaultValue: CHECKBOX_DEFAULT_CAP, isLocalizable: true},
      TTypeConstructor(qualify(Plugins.name('Basic'), typeName('OnBlurText')))
    )
  ),
});

const inputTypeFields = withSystem(
  {
    defaultValue: 'default',
    label: 'Style',
    isLocalizable: true,
  },
  TEnum({default: 'Default', custom: 'Custom'})
);

const attrFields = TRecord({
  type: withSystem({defaultValue: CHECKBOX_DEFAULT, private: true}, TText),
  name: withSystem(
    {defaultValue: CHECKBOX_DEFAULT_CAP, private: true, isLocalizable: true},
    TText
  ),
  id: withSystem(
    {
      defaultValue: CHECKBOX_DEFAULT,
      private: true,
      label: 'ID',
      isBindable: false,
      isLocalizable: true,
    },
    TTypeConstructor(qualify(Plugins.name('Basic'), typeName('IdTextInput')))
  ),
  'data-name': withSystem(
    {defaultValue: CHECKBOX_DEFAULT_CAP, private: true, isLocalizable: true},
    TText
  ),
  required: withSystem(
    {labelVariant: 'hidden', label: 'Required', isLocalizable: true},
    TTypeConstructor(
      qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
    )
  ),
  checked: withSystem(
    {labelVariant: 'hidden', label: 'Start checked', isLocalizable: true},
    TTypeConstructor(
      qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
    )
  ),
});

const tempFields = TRecord({
  checkbox: TRecord({
    isChecked: withSystem({defaultValue: false, private: true}, TBoolean),
    isFocused: withSystem({defaultValue: false, private: true}, TBoolean),
    isFocusedVisible: withSystem(
      {defaultValue: false, private: true},
      TBoolean
    ),
  }),
});

const fields = {
  form: formFields,
  inputType: inputTypeFields,
  attr: attrFields,
  temp: tempFields,
} as const;

function RenderFormCheckboxDataType({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: (arg1?: any) => void;
}) {
  const onChangeAt = React.useCallback(
    (at: ExpressionPath) => extendExpressionEditorActionAtPath(dispatch)(at),
    [dispatch]
  );

  const onNameCommit = React.useCallback(
    // @ts-expect-error - TS7006 - Parameter 'dispatchExpr' implicitly has an 'any' type.
    (dispatchExpr) => {
      const newName =
        Expressions.getText(dispatchExpr?.payload?.newExpression) || '';
      const trimmedName = EText(trim(newName));
      const idString = EText(toStringId(newName) || '');
      dispatch(
        updateExpressionsAction([
          {key: ATTR_ID_PATH, newExpression: idString},
          {key: ATTR_NAME_PATH, newExpression: idString},
          {key: ATTR_DATA_NAME_PATH, newExpression: trimmedName},
          {key: FORM_NAME_PATH, newExpression: trimmedName},
        ])
      );
    },
    [dispatch]
  );

  const expressionEditorAtPath = React.useCallback(
    (path: ExpressionPath) => {
      const exprAt = getIn(expr, path);
      const onDispatch =
        path === FORM_NAME_PATH ? onNameCommit : onChangeAt(path);
      if (!exprAt) {
        throw Error(
          `Failed to get expression at path: ${JSON.stringify(path)}`
        );
      }
      return <ExpressionEditor expression={exprAt} dispatch={onDispatch} />;
    },
    [expr, onChangeAt, onNameCommit]
  );
  const Name = expressionEditorAtPath(FORM_NAME_PATH);
  const InputType = expressionEditorAtPath(INPUT_TYPE_PATH);
  const Required = expressionEditorAtPath(ATTR_REQUIRED_PATH);
  const Checked = expressionEditorAtPath(ATTR_CHECKED_PATH);

  return (
    <>
      <Grid>
        {Name}
        {InputType}
      </Grid>
      {Required}
      {Checked}
    </>
  );
}

const FormCheckboxType = createDevLinkRootAlias({
  params: ['extraAtomFields'],
  fields,
  render: RenderFormCheckboxDataType,
});

export default FormCheckboxType;
